/**
 * Utility functions for handling API errors and providing user-friendly error messages
 */

import i18n from "app/integrations/i18n";

export interface ApiError {
  response?: {
    status: number;
    data?: {
      message?: string;
      error?: string;
      details?: string;
      title?: string;
    };
    statusText?: string;
  };
  message?: string;
  code?: string;
  request?: any;
  config?: any;
  status?: number;
  title?: string;
  type?: string;
  traceId?: string;
}

/**
 * Gets translated error message with fallback to English
 */
const getTranslatedErrorMessage = (key: string, fallback: string): string => {
  try {
    const translated = i18n.t(key);
    return translated !== key ? translated : fallback;
  } catch {
    return fallback;
  }
};

/**
 * Maps HTTP status codes to user-friendly error messages
 */
const normalizeApiError = (error: unknown): ApiError => {
  if (typeof error === "object" && error !== null) {
    return error as ApiError;
  }

  return {
    message: typeof error === "string" ? error : undefined
  };
};

const isString = (value: unknown): value is string => typeof value === "string";

export const getErrorMessage = (error: unknown): string => {
  const apiError = normalizeApiError(error);

  if (process.env.NODE_ENV === "development") {
    console.log("Error structure:", apiError);
  }

  if (isString(apiError.message) && isUserFriendlyMessage(apiError.message)) {
    return apiError.message;
  }

  const responseData = apiError.response?.data;
  let responseMessage: unknown;
  let responseError: unknown;

  if (typeof responseData === "string") {
    responseMessage = responseData;
  } else if (responseData && typeof responseData === "object") {
    responseMessage = (responseData as { message?: unknown }).message;
    responseError = (responseData as { error?: unknown }).error;
  }

  if (isString(responseMessage) && isUserFriendlyMessage(responseMessage)) {
    return responseMessage;
  }

  if (isString(responseError) && isUserFriendlyMessage(responseError)) {
    return responseError;
  }

  const status = apiError.response?.status ?? apiError.status;

  switch (status) {
    case 400:
      return getTranslatedErrorMessage(
        "errors.generic.badRequest",
        "Invalid request. Please check your input and try again."
      );
    case 401:
      return getTranslatedErrorMessage(
        "errors.generic.unauthorized",
        "You are not authorized to perform this action. Please log in again."
      );
    case 403:
      return getTranslatedErrorMessage(
        "errors.generic.forbidden",
        "You don't have permission to perform this action."
      );
    case 404:
      return getTranslatedErrorMessage(
        "errors.generic.notFound",
        "The requested resource was not found."
      );
    case 409:
      return getTranslatedErrorMessage(
        "errors.generic.conflict",
        "This action conflicts with existing data. Please check and try again."
      );
    case 422:
      return getTranslatedErrorMessage(
        "errors.generic.unprocessableEntity",
        "The data provided is invalid. Please review your input."
      );
    case 429:
      return getTranslatedErrorMessage(
        "errors.generic.tooManyRequests",
        "Too many requests. Please wait a moment and try again."
      );
    case 500:
      return getTranslatedErrorMessage(
        "errors.generic.internalServerError",
        "An internal server error occurred. Please try again later."
      );
    case 502:
      return getTranslatedErrorMessage(
        "errors.generic.badGateway",
        "Service temporarily unavailable. Please try again later."
      );
    case 503:
      return getTranslatedErrorMessage(
        "errors.generic.serviceUnavailable",
        "Service is currently under maintenance. Please try again later."
      );
    case 504:
      return getTranslatedErrorMessage(
        "errors.generic.gatewayTimeout",
        "Request timed out. Please check your connection and try again."
      );
    default:
      if (!status) {
        return getTranslatedErrorMessage(
          "errors.generic.networkError",
          "Unable to connect to the server. Please check your internet connection."
        );
      }
      return getTranslatedErrorMessage(
        "errors.generic.unknownError",
        "An unexpected error occurred. Please try again later."
      );
  }
};

const extractResponseErrorMessage = (
  apiError: ApiError
): string | undefined => {
  const responseData = apiError.response?.data;
  let responseMessage: unknown;
  let responseTitle: unknown;
  let responseError: unknown;

  if (typeof responseData === "string") {
    responseMessage = responseData;
  } else if (responseData && typeof responseData === "object") {
    responseMessage = responseData.message;
    responseTitle = responseData.title;
    responseError = responseData.error;
  }

  for (const candidate of [responseMessage, responseTitle, responseError]) {
    if (isString(candidate) && isUserFriendlyMessage(candidate)) {
      return candidate;
    }
  }

  return undefined;
};

export const getPasswordChangeErrorMessage = (error: unknown): string => {
  const apiError = normalizeApiError(error);

  const responseMessage = extractResponseErrorMessage(apiError);
  if (responseMessage) {
    return responseMessage;
  }

  if (isString(apiError.message) && isUserFriendlyMessage(apiError.message)) {
    return apiError.message;
  }

  const status = apiError.response?.status ?? apiError.status;

  switch (status) {
    case 400:
      return getTranslatedErrorMessage(
        "errors.passwordChange.badRequest",
        "Unable to change password. Please check your input and try again."
      );
    case 401:
      return getTranslatedErrorMessage(
        "errors.generic.unauthorized",
        "You are not authorized to perform this action. Please log in again."
      );
    case 403:
      return getTranslatedErrorMessage(
        "errors.passwordChange.forbidden",
        "You don't have permission to change your password."
      );
    case 422:
      return getTranslatedErrorMessage(
        "errors.passwordChange.invalidPassword",
        "The password does not meet the requirements. Please try a different password."
      );
    case 429:
      return getTranslatedErrorMessage(
        "errors.generic.tooManyRequests",
        "Too many requests. Please wait a moment and try again."
      );
    case 500:
    case 502:
    case 503:
    case 504:
      return getTranslatedErrorMessage(
        "errors.passwordChange.failed",
        "Unable to change password at this time. Please try again later."
      );
    default:
      if (!status) {
        return getTranslatedErrorMessage(
          "errors.generic.networkError",
          "Unable to connect to the server. Please check your internet connection."
        );
      }

      return getTranslatedErrorMessage(
        "errors.passwordChange.failed",
        "Unable to change password. Please try again later."
      );
  }
};

/**
 * Checks if a message is user-friendly (doesn't contain technical jargon)
 */
const isUserFriendlyMessage = (message: string): boolean => {
  const technicalTerms = [
    "null pointer",
    "exception",
    "stack trace",
    "internal server error",
    "database",
    "sql",
    "connection timeout",
    "undefined",
    "null",
    "NaN",
    "reference error",
    "type error",
    "request failed",
    "network error",
    "axios error",
    "timeout",
    "econnreset",
    "enotfound",
    "econnrefused",
    "status code",
    "http error"
  ];

  const lowerMessage = message.toLowerCase();

  if (technicalTerms.some((term) => lowerMessage.includes(term))) {
    return false;
  }

  if (/request\s+failed\s+\d+/i.test(message)) {
    return false;
  }

  if (
    /^\d{3}$/.test(message.trim()) ||
    /^(bad request|unauthorized|forbidden|not found|internal server error|bad gateway|service unavailable|gateway timeout)$/i.test(
      message.trim()
    )
  ) {
    return false;
  }

  return true;
};

/**
 * Specific error messages for object-related operations
 */
export const getObjectErrorMessage = (
  error: ApiError,
  operation: "create" | "update" | "delete" = "update"
): string => {
  if (process.env.NODE_ENV === "development") {
    console.log("Object error structure:", error);
  }

  const status = error.response?.status;

  const errorMessage = error.message?.toLowerCase() || "";
  const responseMessage = error.response?.data?.message?.toLowerCase() || "";
  const responseError = error.response?.data?.error?.toLowerCase() || "";

  const isPermissionError =
    errorMessage.includes("permission") ||
    errorMessage.includes("unauthorized") ||
    errorMessage.includes("forbidden") ||
    responseMessage.includes("permission") ||
    responseMessage.includes("unauthorized") ||
    responseMessage.includes("forbidden") ||
    responseError.includes("permission") ||
    responseError.includes("unauthorized") ||
    responseError.includes("forbidden");

  if (status === 500 && isPermissionError) {
    return getTranslatedErrorMessage(
      "errors.objects.updateForbidden",
      `You don't have permission to ${operation} this object.`
    );
  }

  switch (status) {
    case 400:
      return getTranslatedErrorMessage(
        "errors.objects.updateBadRequest",
        `Unable to ${operation} object. Please check that all required fields are filled correctly.`
      );
    case 401:
      return getTranslatedErrorMessage(
        "errors.generic.unauthorized",
        `You are not authorized to ${operation} this object. Please log in again.`
      );
    case 403:
      return getTranslatedErrorMessage(
        "errors.objects.updateForbidden",
        `You don't have permission to ${operation} this object.`
      );
    case 404:
      return getTranslatedErrorMessage(
        "errors.objects.updateNotFound",
        "Object not found. It may have been deleted or moved."
      );
    case 409:
      return getTranslatedErrorMessage(
        "errors.objects.updateConflict",
        `Cannot ${operation} object. This may conflict with existing data.`
      );
    case 422:
      return getTranslatedErrorMessage(
        "errors.objects.updateUnprocessableEntity",
        `Invalid object data. Please review the information and try again.`
      );
    case 500:
      return getTranslatedErrorMessage(
        "errors.objects.updateFailed",
        `Failed to ${operation} object due to a server error. Please try again later.`
      );
    default:
      return getErrorMessage(error);
  }
};

/**
 * Specific error messages for user-related operations
 */
export const getUserErrorMessage = (
  error: ApiError,
  operation: "create" | "update" | "delete" = "create"
): string => {
  if (process.env.NODE_ENV === "development") {
    console.log("User error structure:", error);
  }

  const status = error.response?.status;

  const errorMessage = error.message?.toLowerCase() || "";
  const responseMessage = error.response?.data?.message?.toLowerCase() || "";

  if (
    status === 409 ||
    errorMessage.includes("email") ||
    responseMessage.includes("email")
  ) {
    return getTranslatedErrorMessage(
      "errors.users.emailAlreadyExists",
      "User with this email already exists"
    );
  }

  switch (status) {
    case 400:
      return getTranslatedErrorMessage(
        `errors.users.${operation}BadRequest`,
        `Unable to ${operation} user. Please check that all required fields are filled correctly.`
      );
    case 401:
      return getTranslatedErrorMessage(
        "errors.generic.unauthorized",
        `You are not authorized to ${operation} this user. Please log in again.`
      );
    case 403:
      return getTranslatedErrorMessage(
        `errors.users.${operation}Forbidden`,
        `You don't have permission to ${operation} users.`
      );
    case 404:
      return getTranslatedErrorMessage(
        "errors.users.notFound",
        "User not found. It may have been deleted or moved."
      );
    case 409:
      return getTranslatedErrorMessage(
        `errors.users.${operation}Conflict`,
        `Cannot ${operation} user. This may conflict with existing data.`
      );
    case 422:
      return getTranslatedErrorMessage(
        `errors.users.${operation}BadRequest`,
        `Invalid user data. Please review the information and try again.`
      );
    case 500:
      return getTranslatedErrorMessage(
        `errors.users.${operation}Failed`,
        `Failed to ${operation} user due to a server error. Please try again later.`
      );
    default:
      return getErrorMessage(error);
  }
};

/**
 * Specific error messages for report-related operations
 */
export const getReportErrorMessage = (
  error: ApiError,
  operation: "generate" | "download" | "schedule" | "export" = "generate"
): string => {
  if (process.env.NODE_ENV === "development") {
    console.log("Report error structure:", error);
  }

  const status = error.response?.status;

  switch (status) {
    case 400:
      return getTranslatedErrorMessage(
        "errors.reports.requiredFieldsMissing",
        "Required fields are missing."
      );
    case 401:
      return getTranslatedErrorMessage(
        "errors.generic.unauthorized",
        "You are not authorized to access reports. Please log in again."
      );
    case 403:
      return getTranslatedErrorMessage(
        "errors.generic.forbidden",
        "You don't have permission to access reports."
      );
    case 404:
      return getTranslatedErrorMessage(
        "errors.generic.notFound",
        "Report not found."
      );
    case 500:
      return getTranslatedErrorMessage(
        `errors.reports.${operation}Failed`,
        `Failed to ${operation} report. Please try again later.`
      );
    default:
      return getErrorMessage(error);
  }
};

/**
 * Specific error messages for mileage deviation operations
 */
export const getMileageDeviationErrorMessage = (
  error: ApiError,
  operation: "create" | "update" | "delete" = "update"
): string => {
  if (process.env.NODE_ENV === "development") {
    console.log("Mileage deviation error structure:", error);
  }

  const status = error.response?.status;

  switch (status) {
    case 400:
      return getTranslatedErrorMessage(
        "errors.generic.badRequest",
        "Invalid request. Please check your input and try again."
      );
    case 401:
      return getTranslatedErrorMessage(
        "errors.generic.unauthorized",
        "You are not authorized to modify mileage deviations. Please log in again."
      );
    case 403:
      return getTranslatedErrorMessage(
        "errors.generic.forbidden",
        "You don't have permission to modify mileage deviations."
      );
    case 404:
      return getTranslatedErrorMessage(
        "errors.generic.notFound",
        "Mileage deviation not found."
      );
    case 500:
      return getTranslatedErrorMessage(
        `errors.mileageDeviations.${operation}Failed`,
        `Failed to ${operation} mileage deviation. Please try again later.`
      );
    default:
      return getErrorMessage(error);
  }
};

/**
 * Specific error messages for XAR report operations
 */
export const getXarReportErrorMessage = (
  error: any,
  operation: "generate" | "list" | "download" = "generate",
  reportMode?: "byDriver" | "byObject"
): string => {
  if (process.env.NODE_ENV === "development") {
    console.log("XAR report error structure:", error);
  }

  // Handle both error.response.status (Axios) and error.status (Redux Thunk rejected payload)
  const status = error.status || error.response?.status;
  const errorMessage = error.message?.toLowerCase() || "";
  const errorTitle = error.title?.toLowerCase() || "";
  const responseMessage = error.response?.data?.message?.toLowerCase() || "";
  const responseError = error.response?.data?.error?.toLowerCase() || "";
  const responseTitle = error.response?.data?.title?.toLowerCase() || "";
  const validationErrors = error.errors || error.response?.data?.errors;

  // Check for validation errors (400 with errors object)
  if (status === 400 && validationErrors) {
    // Check if it's a driverId validation error
    if (validationErrors["$.driverId"] || validationErrors.driverId) {
      return "Please select a valid driver to generate the XAR report.";
    }
    // Check if it's an objectIds validation error
    if (validationErrors["$.objectIds"] || validationErrors.objectIds) {
      return "Please select at least one object to generate the XAR report.";
    }
    // Check if request field is required
    if (validationErrors.request) {
      return "XAR report cannot be generated. Please check driver and trip type for the selected period";
    }
  }

  // Check for specific XAR report error conditions
  if (
    errorMessage.includes("incomplete") ||
    errorMessage.includes("driver") ||
    errorMessage.includes("trip") ||
    errorTitle.includes("incomplete") ||
    errorTitle.includes("driver") ||
    errorTitle.includes("trip") ||
    responseMessage.includes("incomplete") ||
    responseMessage.includes("driver") ||
    responseMessage.includes("trip") ||
    responseError.includes("incomplete") ||
    responseError.includes("driver") ||
    responseError.includes("trip") ||
    responseTitle.includes("incomplete") ||
    responseTitle.includes("driver") ||
    responseTitle.includes("trip")
  ) {
    return "XAR report cannot be generated. Please check driver and trip type for the selected period";
  }

  switch (status) {
    case 400:
      // Return specific message for 400 Bad Request
      if (operation === "generate" || operation === "list") {
        if (reportMode === "byDriver") {
          return "Please select a valid driver to generate the XAR report.";
        } else if (reportMode === "byObject") {
          return "Please select at least one object to generate the XAR report.";
        }
        return "XAR report cannot be generated. Please check driver and trip type for the selected period";
      }
      return "XAR report cannot be generated. Please check driver and trip type for the selected period";
    case 401:
      return getTranslatedErrorMessage(
        "errors.generic.unauthorized",
        "You are not authorized to access XAR reports. Please log in again."
      );
    case 403:
      return getTranslatedErrorMessage(
        "errors.generic.forbidden",
        "You don't have permission to access XAR reports."
      );
    case 404:
      if (reportMode === "byDriver") {
        return "No XAR reports found for the selected driver in this period. Please select a different driver or date range.";
      } else if (reportMode === "byObject") {
        return "No XAR reports found for the selected object(s) in this period. Please select different objects or date range.";
      }
      return "No XAR reports found for the selected criteria. Please try different filters.";
    case 500:
      return getTranslatedErrorMessage(
        `errors.reports.${operation}Failed`,
        `Failed to ${operation} XAR report. Please try again later.`
      );
    default:
      return getErrorMessage(error);
  }
};

/**
 * Specific error messages for mileage correction operations
 */
export const getMileageCorrectionErrorMessage = (error: ApiError): string => {
  if (process.env.NODE_ENV === "development") {
    console.log("Mileage correction error structure:", error);
  }

  const status = error.response?.status;
  const errorMessage = error.message?.toLowerCase() || "";
  const responseData = error.response?.data;

  // Get the response message - could be string or object
  let responseMessage = "";
  if (typeof responseData === "string") {
    responseMessage = responseData;
  } else if (responseData?.message) {
    responseMessage = responseData.message;
  } else if (responseData?.error) {
    responseMessage = responseData.error;
  }

  const responseLowerCase = responseMessage.toLowerCase();

  // Helper function to format dates in error messages
  const formatDatesInMessage = (message: string): string => {
    // Match ISO date patterns like 2025-10-18 11:34:00Z or 2025-10-21T01:04:00Z
    const datePattern = /(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2}:\d{2})Z/g;

    return message.replace(datePattern, (match, datePart, timePart) => {
      try {
        const date = new Date(`${datePart}T${timePart}Z`);
        return date.toLocaleString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false
        });
      } catch {
        return match; // Return original if parsing fails
      }
    });
  };

  // If backend provides a specific message, use it with formatted dates
  if (responseMessage && status === 422) {
    return formatDatesInMessage(responseMessage);
  }

  if (status === 422) {
    if (
      errorMessage.includes("no driver or key found") ||
      responseLowerCase.includes("no driver or key found") ||
      errorMessage.includes("no driver or key") ||
      responseLowerCase.includes("no driver or key")
    ) {
      return getTranslatedErrorMessage(
        "errors.mileageCorrection.noDriverOrKey",
        "Mileage correction failed. No driver or key is linked to this trip. Please Link a driver or key and try again."
      );
    }
  }

  if (
    errorMessage.includes("driver") ||
    responseLowerCase.includes("driver") ||
    errorMessage.includes("missing driver") ||
    responseLowerCase.includes("missing driver")
  ) {
    return getTranslatedErrorMessage(
      "errors.mileageCorrection.missingDriver",
      "Mileage correction failed. No driver or key is linked to this trip. Please Link a driver or key and try again."
    );
  }

  if (
    errorMessage.includes("key") ||
    responseLowerCase.includes("key") ||
    errorMessage.includes("missing") ||
    responseLowerCase.includes("missing")
  ) {
    return getTranslatedErrorMessage(
      "errors.mileageCorrection.missingKey",
      "No key found for the selected period. Please verify the key assignment for these dates."
    );
  }

  if (
    errorMessage.includes("no rides") ||
    responseLowerCase.includes("no rides") ||
    errorMessage.includes("rides not found") ||
    responseLowerCase.includes("rides not found")
  ) {
    return getTranslatedErrorMessage(
      "errors.mileageCorrection.noRidesFound",
      "No rides found for the selected driver and date range."
    );
  }

  switch (status) {
    case 400:
      return getTranslatedErrorMessage(
        "errors.mileageCorrection.invalidDateRange",
        "The selected date range is invalid. Please choose a valid date range."
      );
    case 401:
      return getTranslatedErrorMessage(
        "errors.generic.unauthorized",
        "You are not authorized to perform mileage corrections. Please log in again."
      );
    case 403:
      return getTranslatedErrorMessage(
        "errors.generic.forbidden",
        "You don't have permission to perform mileage corrections."
      );
    case 404:
      return getTranslatedErrorMessage(
        "errors.mileageCorrection.driverNotFound",
        "Driver not found for the selected period. Please check driver assignments."
      );
    case 422:
      return getTranslatedErrorMessage(
        "errors.mileageCorrection.missingDriver",
        "We couldn't find any rides for this driver in the selected dates. Please check the driver assignment and date range."
      );
    case 500:
      return getTranslatedErrorMessage(
        "errors.mileageCorrection.correctionFailed",
        "Mileage correction failed. Please try again later."
      );
    default:
      return getTranslatedErrorMessage(
        "errors.mileageCorrection.correctionFailed",
        "Mileage correction failed. Please try again later."
      );
  }
};
